import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Save, X, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

const inp = {
  border: '1px solid var(--border)', borderRadius: 6,
  padding: '6px 10px', fontSize: 13, background: '#fff',
  width: '100%', boxSizing: 'border-box',
};
const btnBlue  = { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
  background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const btnGhost = { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: 13, cursor: 'pointer' };
const L = { fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 3 };

const RESOLVER_TYPES = ['generated', 'stored'];
const CATEGORIES = ['Distribution', 'Location', 'Réparation', 'Annuaire'];

const EMPTY = { nom: '', categorie: 'Distribution', service_handler: '', service_handler_description: '',
  resolver_type: 'generated', resolver_config: '{}', est_actif: true };

function GeneratorForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...initial,
    resolver_config: initial?.resolver_config
      ? (typeof initial.resolver_config === 'string' ? initial.resolver_config : JSON.stringify(initial.resolver_config, null, 2))
      : '{}',
  });
  const [saving, setSaving] = useState(false);
  const [jsonErr, setJsonErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nom.trim()) { toast.error('Nom obligatoire'); return; }
    if (!form.service_handler.trim()) { toast.error('Service handler obligatoire'); return; }
    let rc = {};
    try { rc = JSON.parse(form.resolver_config); setJsonErr(''); }
    catch { setJsonErr('JSON invalide'); return; }
    setSaving(true);
    try { await onSave({ ...form, resolver_config: rc }); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px',
      background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={L}>Nom *</div>
          <input value={form.nom} onChange={e => set('nom', e.target.value)}
            placeholder="Rapport Technique" style={inp} data-testid="pdfgen-nom" />
        </div>
        <div>
          <div style={L}>Catégorie *</div>
          <select value={form.categorie} onChange={e => set('categorie', e.target.value)} style={inp}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div style={L}>Service Handler *</div>
          <input value={form.service_handler} onChange={e => set('service_handler', e.target.value)}
            placeholder="rapport_technique" style={{ ...inp, fontFamily: 'monospace' }}
            data-testid="pdfgen-handler" />
        </div>
        <div>
          <div style={L}>Type de résolution</div>
          <select value={form.resolver_type} onChange={e => set('resolver_type', e.target.value)} style={inp}>
            {RESOLVER_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div style={L}>Description (interne)</div>
        <input value={form.service_handler_description}
          onChange={e => set('service_handler_description', e.target.value)}
          placeholder="Description technique pour les développeurs" style={inp} />
      </div>
      <div>
        <div style={L}>Resolver Config (JSON)</div>
        <textarea value={form.resolver_config}
          onChange={e => { set('resolver_config', e.target.value); setJsonErr(''); }}
          rows={4} style={{ ...inp, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
          data-testid="pdfgen-config" />
        {jsonErr && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>{jsonErr}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.est_actif} onChange={e => set('est_actif', e.target.checked)} />
          Actif
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={onCancel} style={btnGhost}><X size={13} /> Annuler</button>
        <button onClick={handleSubmit} disabled={saving} style={btnBlue} data-testid="pdfgen-save">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

export default function PdfGenerators() {
  const [generators, setGenerators] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState(null);
  const [expanded, setExpanded]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getPdfGenerators();
      setGenerators(r.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (form === 'new') { await api.createPdfGenerator(data); toast.success('Générateur créé'); }
      else { await api.updatePdfGenerator(form.pdf_generator_id, data); toast.success('Mis à jour'); }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Supprimer "${g.nom}" ?`)) return;
    try { await api.deletePdfGenerator(g.pdf_generator_id); toast.success('Supprimé'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const toggleActive = async (g) => {
    try {
      await api.updatePdfGenerator(g.pdf_generator_id, { ...g, est_actif: !g.est_actif,
        resolver_config: typeof g.resolver_config === 'string' ? JSON.parse(g.resolver_config) : g.resolver_config });
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  // Grouper par catégorie
  const grouped = generators.reduce((acc, g) => {
    (acc[g.categorie] = acc[g.categorie] || []).push(g);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Générateurs PDF</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Catalogue des fonctions de génération de PDF disponibles pour les modèles d'emails.
          </p>
        </div>
        <button onClick={() => setForm('new')} style={btnBlue} data-testid="pdfgen-new-btn">
          <Plus size={14} /> Nouveau générateur
        </button>
      </div>

      {form === 'new' && (
        <GeneratorForm onSave={handleSave} onCancel={() => setForm(null)} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
        </div>
      ) : (
        Object.entries(grouped).map(([categorie, items]) => (
          <div key={categorie}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 8 }}>
              {categorie}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map(g => (
                <div key={g.pdf_generator_id} data-testid={`pdfgen-row-${g.pdf_generator_id}`}
                  style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
                    opacity: g.est_actif ? 1 : 0.55 }}>
                  {/* Ligne principale */}
                  <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                    background: form?.pdf_generator_id === g.pdf_generator_id ? '#EFF6FF' : '#FAFAFA' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{g.nom}</span>
                      <span style={{ marginLeft: 10, fontSize: 11, fontFamily: 'monospace',
                        color: 'var(--text-muted)', background: '#F1F5F9', padding: '1px 6px', borderRadius: 4 }}>
                        {g.service_handler}
                      </span>
                      <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 6px', borderRadius: 4,
                        background: g.resolver_type === 'generated' ? '#EFF6FF' : '#F0FDF4',
                        color: g.resolver_type === 'generated' ? '#1D4ED8' : '#166534' }}>
                        {g.resolver_type}
                      </span>
                    </div>
                    <button onClick={() => toggleActive(g)} title={g.est_actif ? 'Désactiver' : 'Activer'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      {g.est_actif
                        ? <ToggleRight size={18} style={{ color: '#16A34A' }} />
                        : <ToggleLeft size={18} style={{ color: '#9CA3AF' }} />}
                    </button>
                    <button onClick={() => setExpanded(expanded === g.pdf_generator_id ? null : g.pdf_generator_id)}
                      style={{ ...btnGhost, padding: '4px 8px' }}>
                      {expanded === g.pdf_generator_id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    <button onClick={() => setForm(g)} style={{ ...btnGhost, padding: '4px 8px' }}
                      data-testid={`pdfgen-edit-${g.pdf_generator_id}`}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(g)}
                      style={{ ...btnGhost, padding: '4px 8px', color: '#EF4444', borderColor: '#FECACA' }}
                      data-testid={`pdfgen-delete-${g.pdf_generator_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {/* Détails techniques */}
                  {expanded === g.pdf_generator_id && (
                    <div style={{ padding: '10px 14px', background: '#F8FAFC',
                      borderTop: '1px solid var(--border)', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {g.service_handler_description && (
                        <div style={{ color: 'var(--text-muted)' }}>{g.service_handler_description}</div>
                      )}
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 11,
                        background: '#1E293B', color: '#E2E8F0', padding: '8px 12px', borderRadius: 6,
                        overflow: 'auto', maxHeight: 120 }}>
                        {JSON.stringify(g.resolver_config, null, 2)}
                      </pre>
                    </div>
                  )}
                  {/* Formulaire d'édition inline */}
                  {form?.pdf_generator_id === g.pdf_generator_id && (
                    <div style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
                      <GeneratorForm initial={g} onSave={handleSave} onCancel={() => setForm(null)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
